/**
 * SKUCertifications 组件
 * 职责: 管理SKU的产品认证信息
 *
 * 重构后结构:
 * - types.ts: 类型定义
 * - constants.ts: 认证选项常量
 * - components/: 子组件 (CertificationTable, CertificationModal)
 */

import React, { useState, useEffect } from 'react';
import { Form, App } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import axios from 'axios';
import { CertificationTable, CertificationModal } from './components';
import type { SKUCertificationsProps, Certification } from './types';

export const SKUCertifications: React.FC<SKUCertificationsProps> = ({ skuId }) => {
    const [form] = Form.useForm();
    const { message, modal } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [certifications, setCertifications] = useState<Certification[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<Certification | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadCertifications();
    }, [skuId]);

    const loadCertifications = async () => {
        try {
            const response = await axios.get(`/api/sku-certifications/?sku_id=${skuId}`);
            const data = Array.isArray(response.data) ? response.data : response.data.results || [];
            setCertifications(data);
        } catch (error) {
            console.error('加载认证记录失败:', error);
        }
    };

    const handleUploadCertificate = async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post('/api/sku-certifications/upload_certificate/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data;
        } catch (error: any) {
            message.error(error.response?.data?.error || '文件上传失败');
            throw error;
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            let certificateFilePath = editingRecord?.certificate_file || '';
            if (fileList.length > 0 && fileList[0].originFileObj) {
                const uploadResult = await handleUploadCertificate(fileList[0].originFileObj);
                certificateFilePath = uploadResult.path;
            }

            const data = {
                sku: skuId,
                country: values.country,
                certification_type: values.certification_type,
                certificate_file: certificateFilePath,
                notes: values.notes || '',
            };

            if (editingRecord) {
                await axios.patch(`/api/sku-certifications/${editingRecord.id}/`, data);
                message.success('更新成功');
            } else {
                await axios.post('/api/sku-certifications/', data);
                message.success('保存成功');
            }

            setIsModalOpen(false);
            form.resetFields();
            setFileList([]);
            setEditingRecord(null);
            loadCertifications();
        } catch (error) {
            console.error('保存失败:', error);
            message.error('保存失败');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingRecord(null);
        form.resetFields();
        setFileList([]);
        setIsModalOpen(true);
    };

    const handleEdit = (record: Certification) => {
        setEditingRecord(record);
        form.setFieldsValue({
            country: record.country,
            certification_type: record.certification_type,
            notes: record.notes,
        });

        if (record.certificate_file) {
            setFileList([
                {
                    uid: '-1',
                    name: record.certificate_file.split('/').pop() || '证书文件',
                    status: 'done',
                    url: record.certificate_file,
                },
            ]);
        }

        setIsModalOpen(true);
    };

    const handleDelete = (record: Certification) => {
        modal.confirm({
            title: '确认删除',
            content: '确定要删除这条认证记录吗？',
            onOk: async () => {
                try {
                    await axios.delete(`/api/sku-certifications/${record.id}/`);
                    message.success('删除成功');
                    loadCertifications();
                } catch (error) {
                    message.error('删除失败');
                }
            },
        });
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        form.resetFields();
        setFileList([]);
        setEditingRecord(null);
    };

    return (
        <div className="sku-certifications">
            <CertificationTable
                certifications={certifications}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <CertificationModal
                open={isModalOpen}
                editingRecord={editingRecord}
                form={form}
                loading={loading}
                fileList={fileList}
                onFileListChange={setFileList}
                onOk={handleSubmit}
                onCancel={handleCancel}
            />
        </div>
    );
};

export default SKUCertifications;
